const PHONE_MASK_STRATEGY = {
  DISABLED: 'disabled',
  COUNTRY_MASK: 'country_mask',
  COUNTRY_SELECTOR_MASK: 'country_selector_mask',
};

const LOCALE_OVERRIDES = {
  ua: 'uk',
};

let intlTelInput = null;
let uiTranslations = null;
let intlTelInputPromise = null;

const resolveLocale = () => {
  const lang = GLOBAL.SYSTEM_LANGUAGE?.language_iso || 'en';

  return LOCALE_OVERRIDES[lang] || lang;
};

const loadIntlTelInput = () => {
  if (!intlTelInputPromise) {
    intlTelInputPromise = Promise.all([
      import('intl-tel-input/intlTelInputWithUtils'),
      import(
        /* webpackExclude: /(index|types)\.js$/ */
        `intl-tel-input-locale/${resolveLocale()}.js`
      ).catch(() => null),
    ]).then(([library, locale]) => {
      intlTelInput = library.default;
      uiTranslations = locale?.default || null;
    });
  }

  return intlTelInputPromise;
};

class IntlTelInputManager {
  constructor() {
    this.instances = new Map();
  }

  updateHiddenInput(element, instance) {
    try {
      const hiddenInput = document.getElementById(`${element.id}-hidden`);
      if (hiddenInput) {
        hiddenInput.value = instance.getNumber();
      }
    } catch (error) {
      console.error(
        `IntlTelInput: Error updating hidden input for ID "${element.id}":`,
        error,
      );
    }
  }

  /**
   * Normalize phone number display after autofill or paste
   * @param {HTMLElement} element - Input element
   * @param {Object} instance - intl-tel-input instance
   */
  normalizePhoneDisplay(element, instance) {
    if (!instance || !element.value) return;
    if (element.dataset.normalizing === 'true') return;
    try {
      if (instance.isValidNumber()) {
        const fullNumber = instance.getNumber();

        if (fullNumber) {
          element.dataset.normalizing = 'true';
          instance.setNumber(fullNumber);

          setTimeout(() => {
            element.dataset.normalizing = 'false';
          }, 0);
        }
      }
    } catch {
      element.dataset.normalizing = 'false';
    }
  }

  /**
   * Setup validation for phone input
   * @param {HTMLElement} element - Input element
   * @param {Object} instance - intl-tel-input instance
   */
  setupValidation(element, instance) {
    let previousHiddenValue = null;

    const pattern = /^[0-9+\- ]+$/;

    const hiddenInput = document.getElementById(`${element.id}-hidden`);
    if (hiddenInput) {
      previousHiddenValue = hiddenInput.value;
    }

    const restrictInput = (e) => {
      const currentValue = element.value;
      let filtered = currentValue.replace(/[^0-9+\- ]/g, '');
      filtered = filtered.replace(/\s+/g, ' ');

      if (filtered !== currentValue) {
        const cursorPosition = element.selectionStart;
        element.value = filtered;
        const newPosition = Math.max(
          0,
          cursorPosition - (currentValue.length - filtered.length),
        );
        element.setSelectionRange(newPosition, newPosition);
      }
    };

    const inputHandler = () => {
      if (!instance) return;
      this.updateHiddenInput(element, instance);

      const updatedHiddenInput = document.getElementById(
        `${element.id}-hidden`,
      );
      const isJSetAttribute = element.classList.contains('j-set-attribute');
      if (
        updatedHiddenInput &&
        updatedHiddenInput.name &&
        window.CheckoutModule &&
        isJSetAttribute
      ) {
        const newValue = updatedHiddenInput.value;
        if (newValue !== previousHiddenValue) {
          const checkoutModule = CheckoutModule.getInstance();

          checkoutModule.setAttributes([
            {
              name: updatedHiddenInput.name,
              value: newValue,
            },
          ]);

          previousHiddenValue = newValue;
        }
      }

      const toggleErrorBox = (element, errorMessage = null) => {
        if (!errorMessage) {
          element.setCustomValidity('');
          element.classList.remove('__error');
          element.classList.add('valid');
          return;
        }

        element.classList.remove('valid');
        element.classList.add('__error');
        element.setCustomValidity(errorMessage);
      };

      if (element.value) {
        if (instance.isValidNumberPrecise() && pattern.test(element.value)) {
          toggleErrorBox(element, null);
        } else {
          const errorCode = instance.getValidationError();
          const errorMessage = this.getErrorMessage(errorCode);
          toggleErrorBox(element, errorMessage);
        }
      } else {
        toggleErrorBox(element, null);
      }
    };

    element.addEventListener('input', (e) => {
      restrictInput(e);
      this.normalizePhoneDisplay(element, instance);
      this.clearValidationError(element);
    });
    element.addEventListener('blur', inputHandler);
    if (GLOBAL.isMobile) {
      element.addEventListener('open:countryselector', () => {
        document.activeElement.blur();
        this.startViewportSync();
      });
      element.addEventListener('close:countryselector', () => {
        this.stopViewportSync();
      });
    }
  }

  syncViewport() {
    const { style } = document.documentElement;

    style.setProperty('--iti-viewport-height', `${visualViewport.height}px`);
    style.setProperty('--iti-viewport-offset', `${visualViewport.offsetTop}px`);
  }

  startViewportSync() {
    if (!window.visualViewport || this.viewportSyncHandler) {
      return;
    }

    this.viewportSyncHandler = () => this.syncViewport();
    this.syncViewport();

    visualViewport.addEventListener('resize', this.viewportSyncHandler);
    visualViewport.addEventListener('scroll', this.viewportSyncHandler);
  }

  stopViewportSync() {
    if (!this.viewportSyncHandler) {
      return;
    }

    visualViewport.removeEventListener('resize', this.viewportSyncHandler);
    visualViewport.removeEventListener('scroll', this.viewportSyncHandler);
    this.viewportSyncHandler = null;

    const { style } = document.documentElement;

    style.removeProperty('--iti-viewport-height');
    style.removeProperty('--iti-viewport-offset');
  }

  clearValidationError(element) {
    if (!element.classList.contains('__error')) return;

    element.setCustomValidity('');
    element.classList.remove('__error');
  }

  setupFullNumberCopy(element, instance) {
    element.addEventListener('copy', (event) => {
      const { selectionStart, selectionEnd, value } = element;

      if (selectionStart !== 0 || selectionEnd !== value.length) {
        return;
      }

      const fullNumber = instance.getNumber();

      if (!fullNumber) {
        return;
      }

      event.preventDefault();
      event.clipboardData.setData('text/plain', fullNumber.replace(/[^\d+]/g, ''));
    });
  }

  /**
   * Get localized error message
   * @param {number} errorCode - Validation error code
   * @returns {string} - Localized error message
   */
  getErrorMessage(errorCode) {
    const E = intlTelInput.VALIDATION_ERROR;
    const errorMap = {
      [E.IS_POSSIBLE]: l10n('Неверный номер'),
      [E.INVALID_COUNTRY_CODE]: l10n('Неверный код страны'),
      [E.TOO_SHORT]: l10n('Номер слишком короткий'),
      [E.TOO_LONG]: l10n('Номер слишком длинный'),
      [E.IS_POSSIBLE_LOCAL_ONLY]: l10n('Неверный номер'),
      [E.INVALID_LENGTH]: l10n('Неверный номер'),
    };

    return errorMap[errorCode] || l10n('Неверный номер');
  }

  /**
   * Get translations for the loaded locale
   * @returns {Object|null} - Translations object or null
   */
  getTranslations() {
    if (!uiTranslations) {
      return null;
    }

    if (resolveLocale() === 'uk') {
      return {
        ...uiTranslations,
        searchPlaceholder: l10n('Пошук'),
        searchEmptyState: l10n('Нічого не знайдено'),
      };
    }

    return uiTranslations;
  }

  /**
   * Check if intlTelInput should be initialized based on phone mask strategy
   * @returns {boolean} - True if intlTelInput should be used
   */
  isIntlTelInputEnabled() {
    return GLOBAL.phone_mask_strategy?.name !== PHONE_MASK_STRATEGY.DISABLED;
  }

  /**
   * Get intlTelInput options based on phone mask strategy
   * @param {Object} customOptions - Custom options to override defaults
   * @returns {Object} - Options for intl-tel-input
   */
  getIntlTelInputOptions(customOptions = {}) {
    const strategy = GLOBAL.phone_mask_strategy;
    const countryCode = GLOBAL.countryCode.toLowerCase();
    const countryCodeFromSettings =
      GLOBAL.countryCodeFromSettings.toLowerCase();
    const translations = this.getTranslations();

    const defaultOptions = {
      initialCountry: countryCode || countryCodeFromSettings,
      countryNameLocale: resolveLocale(),
      onlyCountries: null,
      countrySelectorMode: GLOBAL.isMobile ? 'FULLSCREEN' : 'DROPDOWN',
      placeholderNumberPolicy: 'POLITE',
      strictMode: true,
      excludeCountries: GLOBAL.blackListedCountries,
      countrySearch: true,
      separateDialCode: true,
    };

    if (translations) {
      defaultOptions.uiTranslations = translations;
    }

    if (
      strategy?.name === PHONE_MASK_STRATEGY.COUNTRY_MASK &&
      countryCodeFromSettings
    ) {
      defaultOptions.onlyCountries = [countryCodeFromSettings];
      defaultOptions.countrySelectorMode = 'OFF';
    }

    return { ...defaultOptions, ...customOptions };
  }

  /**
   * Initialize intl-tel-input for element with specified ID
   * @param {string} id - Element ID
   * @param {Object} options - Options for intl-tel-input
   * @returns {Promise<Object|null>} - Instance or null
   */
  async init(id, options = {}) {
    const element = document.getElementById(id);

    if (!element || !this.isIntlTelInputEnabled()) {
      return null;
    }

    try {
      await loadIntlTelInput();

      const intlTelInputOptions = this.getIntlTelInputOptions(options);
      const instance = intlTelInput(element, intlTelInputOptions);

      instance.promise
        .then(() => {
          this.setupValidation(element, instance);
          this.setupFullNumberCopy(element, instance);
          this.updateHiddenInput(element, instance);
        })
        .catch((error) => {
          console.error(
            `IntlTelInput: Error initializing instance for ID "${id}":`,
            error,
          );
        });

      this.instances.set(id, instance);

      return instance;
    } catch (error) {
      console.error(`IntlTelInput: Error initializing for ID "${id}":`, error);
      this.fallbackToPlainInput(element);

      return null;
    }
  }

  /**
   * Keep the submitted value in sync when the library is unavailable
   * @param {HTMLElement} element - Input element
   */
  fallbackToPlainInput(element) {
    const hiddenInput = document.getElementById(`${element.id}-hidden`);

    if (!hiddenInput) {
      return;
    }

    hiddenInput.value = element.value;
    element.addEventListener(
      'input',
      () => (hiddenInput.value = element.value),
    );
  }

  /**
   * Get instance
   * @param {string} id - Element ID
   * @returns {Object|null} - Instance or null
   */
  getInstance(id) {
    return this.instances.get(id) || null;
  }

  /**
   * Remove instance
   * @param {string} id - Element ID
   */
  destroy(id) {
    if (this.instances.has(id)) {
      const instance = this.instances.get(id);
      instance.destroy();
      this.instances.delete(id);
    }
  }
}

window.IntlTelInputManager = new IntlTelInputManager();
